import { useCallback, useMemo, useState } from 'react';

import {
  REVIEW_WIZARD_STEP_ORDER,
  ReviewQuestionStatus,
  ReviewWizardStep,
  type ReviewQuestion,
} from '../logic/reviewTypes';
import { createMockAstraQuestions } from '../logic/mockAstraQuestions';
import {
  validateSubQuestions,
  type SubQuestionViolation,
} from '../../../shared/domain/subQuestionValidation';
import {
  validateQuestionConfig,
  validateCriteria,
  type QuestionConfigViolation,
  type CriteriaViolation,
} from '../../../shared/domain/questionConfigValidation';

/** Public surface of {@link useReviewWizard}. */
export interface ReviewWizardApi {
  /** The full draft list, in display order. */
  readonly questions: readonly ReviewQuestion[];
  /** Client-side id of the currently-selected question. */
  readonly selectedId: string;
  /** The currently-selected question — always a real entry while the list is
   *  non-empty (selection falls back to the first question if it drifts). */
  readonly selectedQuestion: ReviewQuestion | undefined;
  /** Select a question by its client-side id. */
  readonly selectQuestion: (id: string) => void;
  /** Write an edited question back into the list (matched by `id`). The step
   *  components hand up a whole new `ReviewQuestion`. */
  readonly onQuestionChange: (next: ReviewQuestion) => void;
  /** Remove an unpublished draft and reselect a neighbour. */
  readonly deleteQuestion: (id: string) => void;

  /** The active wizard step. */
  readonly currentStep: ReviewWizardStep;
  /** Jump to a specific step. */
  readonly goToStep: (step: ReviewWizardStep) => void;
  /** Advance one step (clamped at the last). */
  readonly goNext: () => void;
  /** Go back one step (clamped at the first). */
  readonly goPrev: () => void;
  /** "Skip for now" — leave this whole question for later and select the next
   *  unpublished draft, wrapping circularly past the end (published drafts are
   *  skipped). No-op only when no other unpublished draft exists. */
  readonly skipForNow: () => void;
  readonly isFirstStep: boolean;
  readonly isLastStep: boolean;

  /** Publish the selected draft: flip it to PUBLISHED, assign a (simulated)
   *  Question ID, and advance selection to the next unpublished draft.
   *  BLOCKED (no state change) when the selected draft has sub-question
   *  violations — see {@link publishBlock}. */
  readonly publishSelected: () => void;

  /** LIVE sub-question violations for the selected draft — recomputed every
   *  render from the draft's current sub-questions, so the naming banner
   *  appears the instant a select sub drops below two options and clears the
   *  instant the author adds the second (no publish click needed to reveal or
   *  clear it). `null` when the selected draft is publishable. The publish gate
   *  reads the same validator, so the two can never disagree. */
  readonly publishBlock: readonly SubQuestionViolation[] | null;

  /** LIVE question-CONFIGURATION violations for the selected draft (overview /
   *  division / audit score / tags), recomputed every render from the draft's
   *  current `configuration`. `null` when the configuration is publishable.
   *  The publish gate reads the same validator. */
  readonly configBlock: readonly QuestionConfigViolation[] | null;

  /** LIVE audit-CRITERIA violations for the selected draft (the five required
   *  rich-text fields), recomputed every render from the draft's current
   *  `content.questionDetails`. `null` when the criteria are publishable. The
   *  publish gate reads the same validator. */
  readonly criteriaBlock: readonly CriteriaViolation[] | null;

  /** Per-step "has blocking validation errors" flags for the selected draft,
   *  keyed by wizard step — AUDIT_CRITERIA ← criteriaBlock, SUB_QUESTIONS ←
   *  publishBlock, QUESTION_CONFIGURATION ← configBlock. The stepper reads this
   *  to show a per-step warning marker. A Map (not a keyed object) so the
   *  union-typed step lookup doesn't trip security/detect-object-injection. */
  readonly stepHasErrors: ReadonlyMap<ReviewWizardStep, boolean>;

  /** Whether the selected draft can be published — true only when ALL three
   *  blocks are clear. The Save & publish button disables on `!canPublish`. */
  readonly canPublish: boolean;
}

/** Reference-stable default for the optional `mandatoryTagGroups` param, so an
 *  omitted argument doesn't re-run the config memo every render. */
const EMPTY_MANDATORY_TAG_GROUPS: readonly string[] = [];

/** First step in the wizard order — the reset/default position. */
const FIRST_STEP = REVIEW_WIZARD_STEP_ORDER[0] ?? ReviewWizardStep.AUDIT_CRITERIA;

/**
 * Generate a simulated published Question ID for a draft.
 *
 * PLACEHOLDER (Phase 1): the real Question ID is returned by the Helios
 * `CreateQuestion` API in Phase 3. Until then, publishing assigns a stable
 * `Q-<n>` derived from how many questions are already published, so the panel
 * has an id to display for a PUBLISHED card.
 */
function simulateQuestionId(publishedCount: number): string {
  return `Q-${1001 + publishedCount}`;
}

/** Pick the id to select after `removedOrPublishedId` leaves the selectable
 *  set: the next selectable (NEEDS_REVIEW) question after it, else the previous
 *  one, else '' when none remain. `list` is the post-transition list. */
function nextSelectableId(list: readonly ReviewQuestion[], anchorIndex: number): string {
  for (let i = anchorIndex; i < list.length; i += 1) {
    const candidate = list.at(i);
    if (candidate && candidate.status === ReviewQuestionStatus.NEEDS_REVIEW) {
      return candidate.id;
    }
  }
  for (let i = anchorIndex - 1; i >= 0; i -= 1) {
    const candidate = list.at(i);
    if (candidate && candidate.status === ReviewQuestionStatus.NEEDS_REVIEW) {
      return candidate.id;
    }
  }
  // No unpublished draft left: fall back to the first question (if any) so the
  // panel still shows a selection, even though its step editors are disabled.
  return list.at(0)?.id ?? '';
}

/**
 * State orchestrator for the "Review & publish Astra questions" wizard.
 *
 * Owns the three pieces of state the (fully-controlled) review components need
 * — the draft list, the selected question id, and the active wizard step — and
 * exposes the actions the page wires to the side panel, the stepper, and the
 * three step editors.
 *
 * Phase 1: seeded from `createMockAstraQuestions()`; publish is a local
 * status flip with a simulated Question ID. Phase 2 swaps the seed for the
 * Astra agent response and Phase 3 routes publish through Helios — the hook's
 * surface stays the same.
 */
export function useReviewWizard(
  /** Tag groups the catalog flags mandatory-for-questions, used by the config
   *  validator's all-mandatory-tags rule. The page sources these from the tags
   *  query and passes them in (the hook owns no data fetching). Defaults to
   *  `[]` so the mandatory rule can't false-positive before the catalog
   *  resolves. Reference-stable at the call site (see the page's memo). */
  mandatoryTagGroups: readonly string[] = EMPTY_MANDATORY_TAG_GROUPS,
  /** SOP link entered on the generate screen, used to prefill each drafted
   *  question's Standard Operating Procedure (configuration.sourcePolicyId).
   *  A plain string (reference-stable); defaults to `''` for direct navigation.
   *  Read once in the seed initializers below, so later changes don't re-seed
   *  (and clobber) edits the author has already made. */
  defaultSourcePolicy = '',
): ReviewWizardApi {
  const [questions, setQuestions] = useState<readonly ReviewQuestion[]>(() =>
    createMockAstraQuestions(defaultSourcePolicy),
  );
  const [selectedId, setSelectedId] = useState<string>(
    () => createMockAstraQuestions(defaultSourcePolicy).at(0)?.id ?? '',
  );
  const [currentStep, setCurrentStep] = useState<ReviewWizardStep>(FIRST_STEP);

  // The selected question, resolved against the current list. Falls back to the
  // first entry if the id drifted (e.g. after a delete) so the step editors —
  // which require a `question` — never receive undefined while drafts remain.
  const selectedQuestion = useMemo(
    () => questions.find((q) => q.id === selectedId) ?? questions.at(0),
    [questions, selectedId],
  );

  // LIVE publish-block: recomputed from the selected draft's current
  // sub-questions on every render (mirrors the perform-audit close gate's
  // derived violations). It shows the instant a select sub has <2 options and
  // clears the instant the author adds the second — no publish click needed to
  // reveal OR to clear it. `null` when the draft is publishable. The publish
  // gate reads the SAME validator, so the banner and the gate can never
  // disagree. (Mid-edit noise is bounded by the options editor's own inline
  // "add at least two options" hint sitting right under the field.)
  const publishBlock = useMemo<readonly SubQuestionViolation[] | null>(() => {
    if (!selectedQuestion) return null;
    const violations = validateSubQuestions(selectedQuestion.content.subQuestions ?? []);
    return violations.length > 0 ? violations : null;
  }, [selectedQuestion]);

  // LIVE configuration block — same live/derived contract as publishBlock, for
  // the question-level config fields. `null` when the configuration is
  // publishable.
  const configBlock = useMemo<readonly QuestionConfigViolation[] | null>(() => {
    if (!selectedQuestion) return null;
    const violations = validateQuestionConfig(
      selectedQuestion.configuration,
      mandatoryTagGroups,
    );
    return violations.length > 0 ? violations : null;
  }, [selectedQuestion, mandatoryTagGroups]);

  // LIVE audit-criteria block — same contract, for the five required rich-text
  // fields. `null` when the criteria are publishable.
  const criteriaBlock = useMemo<readonly CriteriaViolation[] | null>(() => {
    if (!selectedQuestion) return null;
    const violations = validateCriteria(selectedQuestion.content);
    return violations.length > 0 ? violations : null;
  }, [selectedQuestion]);

  // Per-step error flags, derived from the three blocks above — the stepper
  // reads this to mark steps with validation errors. Memoized so the Map
  // identity is stable across renders (the stepper takes it as a prop).
  const stepHasErrors = useMemo<ReadonlyMap<ReviewWizardStep, boolean>>(
    () =>
      new Map([
        [ReviewWizardStep.AUDIT_CRITERIA, criteriaBlock !== null],
        [ReviewWizardStep.SUB_QUESTIONS, publishBlock !== null],
        [ReviewWizardStep.QUESTION_CONFIGURATION, configBlock !== null],
      ]),
    [criteriaBlock, publishBlock, configBlock],
  );

  // The draft is publishable only when every step is clear. The Save & publish
  // button disables on `!canPublish`; the publishSelected gate is the backstop.
  const canPublish =
    criteriaBlock === null && publishBlock === null && configBlock === null;

  const selectQuestion = useCallback((id: string) => {
    setSelectedId(id);
    // Re-entering a draft starts the wizard at the first step.
    setCurrentStep(FIRST_STEP);
  }, []);

  const onQuestionChange = useCallback((next: ReviewQuestion) => {
    setQuestions((prev) => prev.map((q) => (q.id === next.id ? next : q)));
  }, []);

  const deleteQuestion = useCallback(
    (id: string) => {
      // Pure list removal — no selection side effects inside the updater
      // (StrictMode double-invokes updaters; side effects must not live there).
      setQuestions((prev) => prev.filter((q) => q.id !== id));

      // Deleting a draft that ISN'T the selected one leaves selection and step
      // untouched — nothing else to do.
      if (id !== selectedId) return;

      // The selected draft is going away: reselect a neighbour and reset the
      // wizard to the first step, matching selectQuestion / skipForNow /
      // publishSelected (every selection change re-enters at FIRST_STEP).
      // Both decisions are computed here from the id argument, outside the
      // setSelectedId updater.
      const remaining = questions.filter((q) => q.id !== id);
      const removedIndex = questions.findIndex((q) => q.id === id);
      setSelectedId(nextSelectableId(remaining, Math.max(0, removedIndex)));
      setCurrentStep(FIRST_STEP);
    },
    [questions, selectedId],
  );

  const goToStep = useCallback((step: ReviewWizardStep) => {
    setCurrentStep(step);
  }, []);

  const goNext = useCallback(() => {
    setCurrentStep((step) => {
      const index = REVIEW_WIZARD_STEP_ORDER.indexOf(step);
      return REVIEW_WIZARD_STEP_ORDER.at(index + 1) ?? step;
    });
  }, []);

  const goPrev = useCallback(() => {
    setCurrentStep((step) => {
      const index = REVIEW_WIZARD_STEP_ORDER.indexOf(step);
      return index > 0 ? (REVIEW_WIZARD_STEP_ORDER.at(index - 1) ?? step) : step;
    });
  }, []);

  // Leave this whole question for later and jump to the next unpublished draft,
  // searching CIRCULARLY from the current position (published drafts aren't
  // selectable, so they're skipped; the search wraps past the end back to the
  // top). No-op only when there is no OTHER unpublished draft to move to.
  const skipForNow = useCallback(() => {
    const total = questions.length;
    const currentIndex = questions.findIndex((q) => q.id === selectedId);
    for (let offset = 1; offset <= total; offset += 1) {
      const candidate = questions.at((currentIndex + offset) % total);
      if (
        candidate &&
        candidate.id !== selectedId &&
        candidate.status === ReviewQuestionStatus.NEEDS_REVIEW
      ) {
        setSelectedId(candidate.id);
        setCurrentStep(FIRST_STEP);
        return;
      }
    }
  }, [questions, selectedId]);

  const publishSelected = useCallback(() => {
    // Guard once, up-front: publishing a missing or already-published draft is
    // a full no-op — none of the three state writes below may run, or the doc
    // contract ("no-op when already published") breaks (selection would still
    // advance and the step would still reset). Computing the guard here rather
    // than inside the setQuestions updater is also what lets the selection and
    // step writes stay gated together.
    const anchor = questions.findIndex((q) => q.id === selectedId);
    // `anchor < 0` means the selection drifted off the list — nothing to
    // publish. Checked explicitly so the `.at(anchor)` below can't wrap to the
    // last element via a negative index.
    if (anchor < 0) return;
    const target = questions.at(anchor);
    if (!target || target.status === ReviewQuestionStatus.PUBLISHED) return;

    // Validation gate: the draft can't publish while ANY of its three
    // validators report a violation — sub-questions (e.g. a select sub with <2
    // options), the question configuration (overview / division / score /
    // tags), or the audit criteria (the five required rich-text fields). The
    // review flow has no per-field Save, so this is the single enforcement
    // point. The banner already shows all three live (derived above from the
    // same validators), so a blocked click is a no-op — the user is looking at
    // what to fix. Recompute here rather than reading the render-time memos so
    // the guard can't race a stale memo; sharing one pure source of truth keeps
    // banner and gate in lockstep.
    if (
      validateSubQuestions(target.content.subQuestions ?? []).length > 0 ||
      validateQuestionConfig(target.configuration, mandatoryTagGroups).length > 0 ||
      validateCriteria(target.content).length > 0
    ) {
      return;
    }

    const publishedCount = questions.filter(
      (q) => q.status === ReviewQuestionStatus.PUBLISHED,
    ).length;
    // The post-publish list, computed once and reused for both the questions
    // update and the selection decision so the two can't disagree.
    const nextQuestions = questions.map((q) =>
      q.id === selectedId
        ? {
            ...q,
            status: ReviewQuestionStatus.PUBLISHED,
            questionId: simulateQuestionId(publishedCount),
          }
        : q,
    );

    setQuestions(nextQuestions);
    // The just-published question is no longer selectable; look past it. The
    // selection change makes `publishBlock` fall to null (the new draft hasn't
    // been publish-attempted), so no explicit block reset is needed here.
    setSelectedId(nextSelectableId(nextQuestions, anchor + 1));
    setCurrentStep(FIRST_STEP);
  }, [selectedId, questions, mandatoryTagGroups]);

  const currentIndex = REVIEW_WIZARD_STEP_ORDER.indexOf(currentStep);

  return {
    questions,
    selectedId,
    selectedQuestion,
    selectQuestion,
    onQuestionChange,
    deleteQuestion,
    currentStep,
    goToStep,
    goNext,
    goPrev,
    skipForNow,
    isFirstStep: currentIndex <= 0,
    isLastStep: currentIndex === REVIEW_WIZARD_STEP_ORDER.length - 1,
    publishSelected,
    publishBlock,
    configBlock,
    criteriaBlock,
    stepHasErrors,
    canPublish,
  };
}
